import { useState } from "react";
import type { FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { SITE_NAME, SITE_SLOGAN, SITE_DESC } from "@/config/site";
import { organizationSchema, webPageSchema, breadcrumbListSchema } from "@/lib/schema";

const FORM_URL = "https://readdy.ai/api/form/da08q0u859p3u981e13g";

const stats = [
  { value: "1200+", label: "推荐神作" },
  { value: "6 大", label: "题材分类" },
  { value: "98%", label: "读者满意度" },
  { value: "每周", label: "榜单更新" },
];

const testimonials = [
  {
    name: "小林同学",
    role: "追番十年老粉",
    text: "靠着这里的榜单入坑了一堆神作，尤其是机战分类的推荐，简直是我的选番救星！",
  },
  {
    name: "阿禾",
    role: "恋爱番爱好者",
    text: "每次漫荒就看这里的恋爱分类，介绍写得真诚又走心，看完都会心动。",
  },
  {
    name: "大熊",
    role: "上班族二次元",
    text: "内容更新勤快，口碑也很好，帮我省了大量踩雷的时间，强烈推荐给身边朋友。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "关于我们 · 品牌介绍与联系方式",
    description: `了解${SITE_NAME}的品牌理念、读者口碑与联系方式。${SITE_DESC}`,
    path: "/about",
    jsonLd: [
      organizationSchema(),
      webPageSchema("关于我们", "/about", `${SITE_NAME}品牌介绍、口碑与联系方式`),
      breadcrumbListSchema([
        { name: "首页", path: "/" },
        { name: "关于我们", path: "/about" },
      ]),
    ],
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("contact_alt")?.toString().trim() ?? "";
    if (honeypot !== "") {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const message = parsed?.meta?.message ?? "";
      const detail = parsed?.meta?.detail ?? "";
      const lower = `${message} ${detail} ${responseText}`.toLowerCase();

      if (res.ok && parsed?.code === "OK" && !lower.includes("spam")) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(message || detail || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <Navbar />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 md:px-6 md:py-12">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        {/* Brand intro */}
        <section className="mt-6 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary-700">品牌介绍</p>
            <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              关于 {SITE_NAME}
            </h1>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              {SITE_NAME}是一支由资深二次元爱好者组成的选番团队。我们深知"漫荒"的滋味，
              因此决定用专业而真诚的方式，帮大家从海量作品中快速锁定对胃口的日漫神作。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              我们覆盖{` `}日漫恋爱、日漫热血、日漫悬疑、日漫治愈、日漫机战等热门题材，
              榜单持续更新，简介真诚走心，拒绝标题党，只为给你值得花时间的好作品。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              我们的使命：{SITE_SLOGAN}，让每一份热爱都被温柔以待。
            </p>
          </div>
          <div className="overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=cozy%20editorial%20illustration%20of%20an%20anime%20review%20workspace%20with%20notebooks%20posters%20and%20warm%20desk%20lamp%20light%2C%20bookshelves%20with%20manga%20volumes%2C%20cinematic%20warm%20tones%2C%20high%20quality%20digital%20art&width=900&height=640&seq=about-team&orientation=landscape"
              alt="日漫精选推荐团队的选番工作室"
              title={`${SITE_NAME}工作室`}
              className="h-full w-full object-cover"
            />
          </div>
        </section>

        {/* Reputation stats */}
        <section className="mt-14 rounded-lg bg-background-100 p-8 md:p-10">
          <h2 className="text-center font-heading text-2xl font-bold text-foreground-950">
            我们值得信赖
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-center text-sm text-foreground-600">
            以口碑立身，以内容为荣。以下是我们的读者数据与真实评价。
          </p>
          <dl className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <dd className="font-heading text-3xl font-black text-primary-700">{s.value}</dd>
                <dt className="mt-1 text-sm text-foreground-600">{s.label}</dt>
              </div>
            ))}
          </dl>
        </section>

        {/* Testimonials */}
        <section className="mt-14">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">读者口碑</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex gap-0.5 text-accent-500" aria-label="五星好评">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-6 text-foreground-700">{t.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-sm font-bold text-secondary-900">
                    {t.name.slice(0, 1)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mt-14 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm leading-6 text-foreground-600">
              有想看的题材、想推荐的宝藏番，或是对我们内容的想法？欢迎随时来信，我们很乐意倾听。
            </p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className="ri-mail-line" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-semibold text-foreground-950">邮箱</p>
                  <p className="text-foreground-600">hello@riman.example.com</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-800">
                  <i className="ri-chat-3-line" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-semibold text-foreground-950">官方社群</p>
                  <p className="text-foreground-600">日漫精选交流群 · 每日神作分享</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-900">
                  <i className="ri-time-line" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-semibold text-foreground-950">回复时间</p>
                  <p className="text-foreground-600">工作日 09:00 - 18:00</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h3>
            {status === "success" && (
              <p className="mt-3 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                感谢你的来信！我们会尽快回复你。
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">{formError}</p>
            )}
            <form
              data-readdy-form="联系我们表单"
              className="mt-5 space-y-4"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col gap-4 sm:flex-row">
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="你的称呼"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                />
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="你的邮箱"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                />
              </div>
              <input
                type="text"
                name="contact_alt"
                aria-hidden="true"
                tabIndex={-1}
                autoComplete="off"
                readOnly
                className="contact-alt"
              />
              <textarea
                name="message"
                required
                maxLength={500}
                placeholder="想对我们说的话（500 字以内）"
                rows={5}
                className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
              />
              <button
                type="submit"
                className="w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                提交留言
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}